import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Select } from './styles'
import { ISelect } from './interface'
import { getDataOfType } from 'src/lib/utils/get-data-of-type'
import { Button, Checkbox, Divider, Space, Tooltip } from 'antd'
import { DescriptionTooltipIcon } from '../description-tooltip-icon'

function filterOption(input: string, option: any): boolean {
  const optionText = option?.label ?? option?.children
  return String(optionText ?? '')
    .toLowerCase()
    .includes(input.toLowerCase())
}

function SelectFromEnum({ value, schema, onChange, UISchema }: ISelect) {
  const [localValue, setLocalValue] = useState<any>()
  const isMultiple = useMemo(() => Boolean(UISchema?.options?.multiple), [UISchema])
  const [all, setAll] = useState<boolean>(false)
  const allowLongText = useMemo(() => Boolean(UISchema?.options?.allowLongText), [UISchema])

  useEffect(() => {
    if (isMultiple) {
      setLocalValue(value => value?.filter(v => Boolean(v)))
    } else {
      setLocalValue(value === undefined ? null : value)
    }
  }, [value])

  const handleChange = (newValue: any) => {
    const normalizedValue =
      newValue === undefined || (Array.isArray(newValue) && newValue.length === 0) ? null : newValue
    setLocalValue(normalizedValue)
    onChange?.(normalizedValue)
  }

  const props = useMemo(() => {
    const props = {
      width: UISchema?.options?.width,
      allowClear: Boolean(UISchema?.options?.allowClear),
      showSearch: Boolean(UISchema?.options?.showSearch),
      placeholder: UISchema?.options?.placeholder ?? 'Выберите запись',
      optionFilterProp: 'children',
      optionLabelProp: 'children',
      onChange: handleChange,
      defaultValue: value,
      filterOption,
      disabled: Boolean(UISchema?.options?.disabled) || all,
      style: UISchema?.style,
    }
    if (isMultiple) {
      Object.assign(props, { mode: 'multiple' })
    }
    return props
  }, [UISchema, all])

  const objects: any[] = useMemo(() => {
    const values = getDataOfType(schema, 'enum', Array, [])
    const oneOf = getDataOfType(schema, 'oneOf', Array, [])
    const oneOfObject = Object.fromEntries(oneOf.map(item => [item['const'], item]))
    return values.map(value => {
      const option = oneOfObject[value]
      return {
        name: value,
        title: getDataOfType(option, 'title', [String, Number], value),
        description: getDataOfType(option, 'description', String, null),
        disabled: Boolean(option?.disabled),
      }
    })
  }, [schema])

  const afterButton: any = useMemo(() => {
    return UISchema?.options?.afterButton || null
  }, [UISchema])

  const selectAll: any = useMemo(() => {
    return Boolean(UISchema?.options?.allowSelectAll || schema?.options?.allowSelectAll)
  }, [UISchema, schema])

  const handleSelectAll = useCallback(
    e => {
      const all = e.target.checked
      setAll(all)
      if (all) {
        const newValue = objects.map(object => object.name)
        const normalizedValue =
          newValue === undefined || (Array.isArray(newValue) && newValue.length === 0)
            ? undefined
            : newValue
        setLocalValue(normalizedValue)
        onChange?.(normalizedValue)
      } else {
        setLocalValue([])
        onChange?.([])
      }
    },
    [objects, setAll],
  )

  return (
    <Tooltip trigger={['hover']} title={UISchema?.tooltip || null} placement="topLeft">
      <div
        style={{
          display: 'flex',
          flexDirection: selectAll ? 'column' : undefined,
        }}
      >
        <Select
          {...props}
          value={localValue}
          $multiline={allowLongText}
          popupRender={
            UISchema?.options?.footer_item
              ? menu => (
                  <>
                    {menu}
                    <Divider style={{ margin: '0' }} />
                    <Space style={{ padding: '8px', display: 'block' }}>
                      <Button href={UISchema.options.footer_item.url} block>
                        {UISchema.options.footer_item.title}
                      </Button>
                    </Space>
                  </>
                )
              : undefined
          }
        >
          {objects.map(item => (
            <Select.Option
              key={item['name']}
              value={item['name']}
              disabled={item?.disabled}
              label={item['title']}
              title={item['description'] || item['title']}
            >
              {item['description'] ? (
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    width: '100%',
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {item['title']}
                  </span>
                  <DescriptionTooltipIcon description={item['description']} placement="right" />
                </span>
              ) : (
                item['title']
              )}
            </Select.Option>
          ))}
        </Select>
        {isMultiple && selectAll ? (
          <>
            <Checkbox onChange={handleSelectAll}>Выбрать все</Checkbox>
          </>
        ) : null}
        {afterButton}
      </div>
    </Tooltip>
  )
}

export default SelectFromEnum
